/**
 * MediaStore — IndexedDB-based persistence for media files in drafts.
 *
 * All new uploads (images AND videos) are stored as blob URLs in memory.
 * This module persists the actual File objects in IndexedDB keyed by
 * draftId, preserving them across browser sessions on the same device.
 *
 * Limitations:
 * - Browser-local only — files won't sync across devices.
 * - Cleared if the user clears browser data.
 */

const DB_NAME = 'be-creator-media';
const STORE_NAME = 'draft-media';

interface StoredMediaEntry {
  /** Composite key: `${draftId}::${originalIndex}` */
  key: string;
  file: File;
  originalIndex: number;
  filename: string;
  mimeType: string;
}

/** Single entry in an image manifest — describes either an HTTP or blob image. */
export interface ManifestEntry {
  kind: 'http' | 'blob';
  /** HTTP URL (only for kind === 'http') */
  url?: string;
  /** Key into IndexedDB media store (only for kind === 'blob') */
  blobKey?: string;
  /** Original filename */
  filename?: string;
  caption?: string;
  copyright?: string;
}

interface StoredManifest {
  /** Key: `manifest::${draftId}` */
  key: string;
  entries: ManifestEntry[];
}

const MANIFEST_STORE = 'image-manifests';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 2);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(MANIFEST_STORE)) {
        db.createObjectStore(MANIFEST_STORE, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Store media files for a draft. Replaces any previously stored files
 * for the same draftId.
 */
export async function storeMediaForDraft(
  draftId: string,
  media: Array<{ file: File; originalIndex: number }>
): Promise<void> {
  if (!draftId || media.length === 0) return;

  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);

  // Clear existing entries for this draft first
  await new Promise<void>((resolve, reject) => {
    const cursorReq = store.openCursor();
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (cursor) {
        const entry = cursor.value as StoredMediaEntry;
        if (entry.key.startsWith(`${draftId}::`)) {
          cursor.delete();
        }
        cursor.continue();
      } else {
        resolve();
      }
    };
    cursorReq.onerror = () => reject(cursorReq.error);
  });

  // Store new entries
  for (const item of media) {
    const entry: StoredMediaEntry = {
      key: `${draftId}::${item.originalIndex}`,
      file: item.file,
      originalIndex: item.originalIndex,
      filename: item.file.name,
      mimeType: item.file.type,
    };
    store.put(entry);
  }

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

/**
 * Retrieve all stored media files for a given draftId.
 * Returns entries sorted by originalIndex.
 */
export async function getMediaForDraft(
  draftId: string
): Promise<Array<{ file: File; originalIndex: number; filename: string; mimeType: string }>> {
  if (!draftId) return [];

  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const results: Array<{ file: File; originalIndex: number; filename: string; mimeType: string }> = [];

    return new Promise((resolve, reject) => {
      const cursorReq = store.openCursor();
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (cursor) {
          const entry = cursor.value as StoredMediaEntry;
          if (entry.key.startsWith(`${draftId}::`)) {
            results.push({
              file: entry.file,
              originalIndex: entry.originalIndex,
              filename: entry.filename,
              mimeType: entry.mimeType,
            });
          }
          cursor.continue();
        } else {
          db.close();
          resolve(results.sort((a, b) => a.originalIndex - b.originalIndex));
        }
      };
      cursorReq.onerror = () => { db.close(); reject(cursorReq.error); };
    });
  } catch (error) {
    console.warn('[mediaStore] Failed to read from IndexedDB:', error);
    return [];
  }
}

/**
 * Remove all stored media and manifest for a draft (e.g. after publish or delete).
 */
export async function clearMediaForDraft(draftId: string): Promise<void> {
  if (!draftId) return;

  try {
    const db = await openDB();
    const tx = db.transaction([STORE_NAME, MANIFEST_STORE], 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const manifestStore = tx.objectStore(MANIFEST_STORE);

    // Delete manifest
    manifestStore.delete(`manifest::${draftId}`);

    // Delete media entries
    await new Promise<void>((resolve, reject) => {
      const cursorReq = store.openCursor();
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (cursor) {
          const entry = cursor.value as StoredMediaEntry;
          if (entry.key.startsWith(`${draftId}::`)) {
            cursor.delete();
          }
          cursor.continue();
        } else {
          resolve();
        }
      };
      cursorReq.onerror = () => reject(cursorReq.error);
    });

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    });
  } catch (error) {
    console.warn('[mediaStore] Failed to clear from IndexedDB:', error);
  }
}

/**
 * Store an image manifest for a draft. Records the full image list
 * structure (both http and blob entries) to preserve ordering.
 */
export async function storeManifestForDraft(
  draftId: string,
  entries: ManifestEntry[]
): Promise<void> {
  if (!draftId) return;

  try {
    const db = await openDB();
    const tx = db.transaction(MANIFEST_STORE, 'readwrite');
    const store = tx.objectStore(MANIFEST_STORE);

    const manifest: StoredManifest = {
      key: `manifest::${draftId}`,
      entries,
    };
    store.put(manifest);

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    });
  } catch (error) {
    console.warn('[mediaStore] Failed to store manifest:', error);
  }
}

/**
 * Retrieve the image manifest for a draft, or null if none exists (legacy draft).
 */
export async function getManifestForDraft(
  draftId: string
): Promise<ManifestEntry[] | null> {
  if (!draftId) return null;

  try {
    const db = await openDB();
    const tx = db.transaction(MANIFEST_STORE, 'readonly');
    const store = tx.objectStore(MANIFEST_STORE);

    return new Promise((resolve, reject) => {
      const req = store.get(`manifest::${draftId}`);
      req.onsuccess = () => {
        db.close();
        const result = req.result as StoredManifest | undefined;
        resolve(result?.entries ?? null);
      };
      req.onerror = () => { db.close(); reject(req.error); };
    });
  } catch (error) {
    console.warn('[mediaStore] Failed to read manifest:', error);
    return null;
  }
}
